import React, { useState } from 'react'
import type { DisplayMode } from '@/services/clinicProgramService'
import ConfirmDisplayModeChangeDialog from '@/components/ui/confirm-display-mode-change-dialog'

export interface DisplayModeToggleProps {
  currentMode: DisplayMode
  programId: string
  clinicId: string
  programTitle: string
  onUpdate: (mode: DisplayMode) => Promise<void>
  disabled?: boolean
}

const DisplayModeToggle: React.FC<DisplayModeToggleProps> = ({
  currentMode,
  programId,
  clinicId,
  programTitle,
  onUpdate,
  disabled = false,
}) => {
  const [isUpdating, setIsUpdating] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingMode, setPendingMode] = useState<DisplayMode | null>(null)
  const isGuided = currentMode === 'protocol_showcase'

  const handleToggle = () => {
    if (isUpdating || disabled) return
    const next: DisplayMode = isGuided ? 'medication_select' : 'protocol_showcase'
    setPendingMode(next)
    setConfirmOpen(true)
  }

  const handleConfirm = async () => {
    if (!pendingMode) return
    setIsUpdating(true)
    try {
      await onUpdate(pendingMode)
    } finally {
      setIsUpdating(false)
      setPendingMode(null)
    }
  }

  const handleCancel = () => {
    setConfirmOpen(false)
    setPendingMode(null)
  }

  return (
    <>
      <div className='flex items-center gap-2 text-xs'>
        <span className={!isGuided ? 'font-semibold text-slate-900' : 'text-slate-400'}>
          In-house
        </span>
        <button
          type='button'
          aria-label={isGuided ? 'Switch to In-house mode' : 'Switch to Nimbus-Routed mode'}
          aria-checked={isGuided}
          role='switch'
          disabled={isUpdating || disabled}
          className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors disabled:opacity-50 ${
            isGuided ? 'bg-blue-500' : 'bg-slate-300'
          }`}
          onClick={handleToggle}
          data-program-id={programId}
          data-clinic-id={clinicId}
        >
          <span
            className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
              isGuided ? 'translate-x-5' : 'translate-x-1'
            }`}
          />
        </button>
        <span className={isGuided ? 'font-semibold text-blue-600' : 'text-slate-400'}>
          Nimbus-Routed
        </span>
      </div>

      {pendingMode && (
        <ConfirmDisplayModeChangeDialog
          open={confirmOpen}
          onOpenChange={(open) => { if (!open) handleCancel() }}
          programTitle={programTitle}
          fromMode={currentMode}
          toMode={pendingMode}
          onConfirm={handleConfirm}
          isLoading={isUpdating}
        />
      )}
    </>
  )
}

export default DisplayModeToggle
